import * as React from 'react'
import styled from 'styled-components'
import * as constants from 'static/constants'
import * as interfaces from 'static/interfaces'
import { Drawer } from 'components/atoms'
import { IBuildedTour } from 'utils/weeklyTypes'
import { GuidePanel } from './GuidePanel'
import { TourDrawerHeader } from './TourDrawerHeader'
import { TourDrawerLeftPanel } from './TourDrawerLeftPanel'
import GuestPanel from './GuestPanel'
import { useTourDrawerContext, GROUP_CATEGORY } from 'core/contexts/tour/TourDrawerContext'
import { sortedOtaReservations } from 'utils/weekly'

export declare var window: interfaces.IWindow

interface IProps {
  currentUser: interfaces.IUser
  tour: IBuildedTour
  show: boolean
  refetchWeeklyTour: (tour_id: number) => void
  handleClose: any
}

const TourDrawer: React.VFC<IProps> = React.memo(
  ({ currentUser, tour, show, refetchWeeklyTour, handleClose }) => {
    const {
      selectedGroupName,
      setSelectedGroupName,
      selectedTourGroup,
      setSelectedTourGroup,
      setSelectedOtaReservations,
      orderDesc,
      setJustAssignedOtaReservations,
    } = useTourDrawerContext()

    const handleSelectTourGroup = React.useCallback(
      tourGroup => {
        setSelectedTourGroup(tourGroup)
        setSelectedOtaReservations(
          sortedOtaReservations(
            tour.ongoingOtaReservations.filter(
              otaReservation => otaReservation.jwt_tour_group_id === tourGroup.id
            ),
            orderDesc
          )
        )
        setSelectedGroupName(GROUP_CATEGORY.TOUR_GROUP)
      },
      [tour.ongoingOtaReservations, orderDesc]
    )

    const handleSelectUnassignedOtaReservations = React.useCallback(() => {
      setSelectedOtaReservations(sortedOtaReservations(tour.unassignedOtaReservations, orderDesc))
      setSelectedTourGroup(null)
      setSelectedGroupName(GROUP_CATEGORY.UNASSIGNED)
    }, [tour.unassignedOtaReservations, orderDesc])

    const handleSelectCanceledOtaReservations = React.useCallback(() => {
      setSelectedOtaReservations(sortedOtaReservations(tour.canceledOtaReservations, orderDesc))
      setSelectedTourGroup(null)
      setSelectedGroupName(GROUP_CATEGORY.CANCELED)
    }, [tour.canceledOtaReservations, orderDesc])

    const handleClearSelect = React.useCallback(() => {
      setSelectedOtaReservations(sortedOtaReservations(tour.ongoingOtaReservations, orderDesc))
      setSelectedTourGroup(null)
      setSelectedGroupName(null)
    }, [
      tour.ongoingOtaReservations,
      orderDesc,
      setSelectedTourGroup,
      setSelectedOtaReservations,
      setSelectedGroupName,
    ])

    React.useEffect(() => {
      if (tour) {
        // 選択中の予約リストを更新
        if (selectedGroupName === GROUP_CATEGORY.TOUR_GROUP) {
          handleSelectTourGroup(selectedTourGroup)
        } else if (selectedGroupName === GROUP_CATEGORY.UNASSIGNED) {
          handleSelectUnassignedOtaReservations()
        } else if (selectedGroupName === GROUP_CATEGORY.CANCELED) {
          handleSelectCanceledOtaReservations()
        } else if (selectedGroupName === null) {
          handleClearSelect()
        }
      }
    }, [tour.ota_reservations])

    React.useEffect(() => {
      setJustAssignedOtaReservations([])
    }, [selectedGroupName])

    return (
      <Drawer
        show={show}
        position="right"
        width="xl"
        backItemClickable={false}
        handleClose={handleClose}
      >
        <Wrapper>
          <TourDrawerHeader
            tour={tour}
            refetchWeeklyTour={refetchWeeklyTour}
            handleClose={handleClose}
          />
          <div className="main">
            <TourDrawerLeftPanel
              currentUser={currentUser}
              tour={tour}
              handleSelectTourGroup={handleSelectTourGroup}
              handleSelectUnassignedOtaReservations={handleSelectUnassignedOtaReservations}
              refetchWeeklyTour={refetchWeeklyTour}
            />
            <div className="panel border">
              <GuestPanel tour={tour} handleClearSelect={handleClearSelect} />
            </div>
            <div className="panel border">
              <GuidePanel currentUser={currentUser} tour={tour} />
            </div>
          </div>
        </Wrapper>
      </Drawer>
    )
  }
)
const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  .count {
    color: ${constants.COLORS.primary};
  }
  .main {
    display: grid;
    position: relative;
    flex: 1 1 auto;
    overflow: hidden;
    grid-template-columns: 36% 36% 28%;
    .title {
      flex: 0 0 auto;
      position: sticky;
      top: 0;
      display: flex;
      justify-content: space-between;
      line-height: 32px;
      font-size: 14px;
      font-weight: bold;
      background-color: ${constants.COLORS.background2};
      padding: 8px 16px;
      transition: background-color 0.2s;
      z-index: 3;
      &.bordered {
        border-top: 1px solid ${constants.COLORS.divider};
      }
      &.groupSelected {
        background-color: ${constants.COLORS.successBG2};
      }

      .category {
        display: flex;
        align-items: center;
        color: ${constants.COLORS.subText};
        .sortButton {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: 4px;
          transition: opacity 0.2s;
          cursor: pointer;
          &:hover {
            opacity: 0.75;
          }
        }
      }

      .titleRight {
        display: flex;
        align-items: center;

        .addTourGroupBtn {
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          margin-left: 6px;
          color: ${constants.COLORS.divider};
          transition: color 0.2s;
          svg {
            width: 20px;
            height: 20px;
          }
          &:hover {
            color: ${constants.COLORS.primary};
          }
        }
      }
      .Button {
        display: flex;
        align-items: center;
        line-height: 24px;
        padding: 4px 16px 4px 12px;
        .icon {
          width: 20px;
          height: 20px;
          margin-right: 4px;
        }
      }
    }

    .panel {
      position: relative;
      height: 100%;
      z-index: 0;
      &.border {
        border-left: 1px solid ${constants.COLORS.divider};
      }
    }

    // ドロワー内のメモ: 破線ボーダーを外し、余白を詰める
    .memo {
      border: 0;
      padding-top: 0;
      margin-top: 8px;
      &:first-child {
        margin-top: 0;
      }
    }
    .memoButton:first-child {
      margin-top: 0;
    }
  }
`

export default TourDrawer
